import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";

import { Bot, List, Loader2, Plus, Table } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

import HazardsTable from "./HazardsTable";
import HazardsList from "./HazardsList";

import HazardSearchFilter from "../Layout/HazardSearchFilter";
import { CommonDialog } from "../../components/CommonDialog";

import useProject from "../../components/hooks/useProject";
import { useToast } from "../../components/hooks/use-toast";
import { useAiModel } from "../../components/hooks/useAiModel";
import { useHazardGenerator } from "../../components/hooks/useHazardGenerator";
import useRiskMatrix from "../../components/hooks/useRiskMatrix";

import { auth } from "../../firebase";

import firestoreService from "../../services/firestoreService";
import functionsService from "../../services/functionsService";
import analyticsService from "../../services/analyticsService";

function Hazards() {
  const { projectId } = useParams();
  const { project } = useProject(projectId);
  const hazardsListRef = useRef(null);
  const hazardsTableRef = useRef(null);
  const { toast } = useToast();
  const { riskMatrix } = useRiskMatrix(project, toast);

  const [activeTab, setActiveTab] = useState(() => {
    const stored = localStorage.getItem(`ui-preference-${projectId}`);
    return stored ? JSON.parse(stored) : "list";
  });

  const [showHazardGenerationWarning, setShowHazardGenerationWarning] =
    useState(false);
  const [hazards, setHazards] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [isEditor, setIsEditor] = useState(false);
  const [filteredHazards, setFilteredHazards] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    searchTerm: "",
    unmitigatedRisk: [],
    retainedRisk: [],
    riskStatus: [],
    mitigationStatus: [],
  });

  const [selectedAiModel] = useAiModel();
  const [isGeneratingHazards, setIsGeneratingHazards] =
    useHazardGenerator(false);

  // Update the Project Title in the Layout
  useEffect(() => {
    if (project?.name) {
      const titleElement = document.getElementById("page-title");

      if (titleElement) {
        titleElement.textContent = `Safety Log for ${project.name}`;
      }
    }

    // Cleanup when component unmounts for Layout
    return () => {
      const titleElement = document.getElementById("page-title");

      if (titleElement) {
        titleElement.textContent = "";
      }
    };
  }, [project?.name]);

  useEffect(() => {
    if (project) {
      // Set user's authority to edit project/hazards
      setIsOwner(project.roles.owners.includes(auth.currentUser.uid));
      setIsEditor(project.roles.editors.includes(auth.currentUser.uid));
    }
  }, [project]);

  // Set up a real-time listener to the Firestore collection on component mount
  useEffect(() => {
    const unsubscribe = firestoreService.subscribeToCollection(
      `projects/${projectId}/hazards`,
      [],
      (hazards) => {
        hazards.sort((a, b) => {
          return a.hazardId < b.hazardId ? -1 : a.hazardId > b.hazardId ? 1 : 0;
        });

        setHazards(hazards);
      }
    );

    // Cleanup the listener on component unmount
    return () => unsubscribe();
  }, [projectId]);

  // Filter Data

  useEffect(() => {
    setFilteredHazards(hazards);
  }, [hazards]);

  useEffect(() => {
    let filtered = [...hazards];

    // Text search
    if (activeFilters.searchTerm) {
      const searchTerm = activeFilters.searchTerm.toLowerCase();

      filtered = filtered.filter(
        (hazard) =>
          hazard.hazardId?.toLowerCase().includes(searchTerm) ||
          hazard.type?.toLowerCase().includes(searchTerm) ||
          hazard.hazardSource?.toLowerCase().includes(searchTerm) ||
          hazard.description?.toLowerCase().includes(searchTerm) ||
          hazard.causes?.some((cause) =>
            cause.toLowerCase().includes(searchTerm)
          ) ||
          hazard.consequences?.some((consequence) =>
            consequence.toLowerCase().includes(searchTerm)
          ) ||
          hazard.mitigations?.some(
            (mitigation) =>
              mitigation.description?.toLowerCase().includes(searchTerm) ||
              mitigation.safetyRequirement
                ?.toLowerCase()
                .includes(searchTerm)
          ) ||
          hazard.sfairpStatement?.toLowerCase().includes(searchTerm) ||
          hazard.residualRiskDescription?.toLowerCase().includes(searchTerm) ||
          // Project Metadata fields on the hazard
          hazard.systems?.some((system) =>
            system.toLowerCase().includes(searchTerm)
          ) ||
          hazard.operatingModes?.some((mode) =>
            mode.toLowerCase().includes(searchTerm)
          ) ||
          hazard.locations?.some((location) =>
            location.toLowerCase().includes(searchTerm)
          ) ||
          hazard.projectLifecycles?.some((lifecycle) =>
            lifecycle.toLowerCase().includes(searchTerm)
          ) ||
          hazard.disciplines?.some((discipline) =>
            discipline.toLowerCase().includes(searchTerm)
          ) ||
          hazard.drawingPackages?.some((pkg) =>
            pkg.toLowerCase().includes(searchTerm)
          ) ||
          hazard.users?.some((user) =>
            user.toLowerCase().includes(searchTerm)
          ) ||
          hazard.riskCategory?.some((category) =>
            category.toLowerCase().includes(searchTerm)
          )
      );
    }

    // Unmitigated Risk filter
    if (activeFilters.unmitigatedRisk?.length > 0) {
      filtered = filtered.filter((hazard) =>
        activeFilters.unmitigatedRisk.includes(hazard.unmitigatedRisk?.rating)
      );
    }

    // Retained Risk filter
    if (activeFilters.retainedRisk?.length > 0) {
      filtered = filtered.filter((hazard) =>
        activeFilters.retainedRisk.includes(hazard.retainedRisk?.rating)
      );
    }

    // Risk Status filter
    if (activeFilters.riskStatus?.length > 0) {
      filtered = filtered.filter((hazard) =>
        activeFilters.riskStatus.includes(hazard.riskStatus)
      );
    }

    // Mitigation Status filter
    if (activeFilters.mitigationStatus?.length > 0) {
      filtered = filtered.filter((hazard) =>
        hazard.mitigations?.some((m) =>
          activeFilters.mitigationStatus.includes(m.status)
        )
      );
    }

    setFilteredHazards(filtered);
  }, [hazards, activeFilters]);

  const generateHazards = () => {
    if (hazards.length > 0) {
      setShowHazardGenerationWarning(true);
    } else {
      doGenerateHazards();
    }
  };

  const doGenerateHazards = useCallback(async () => {
    setIsGeneratingHazards(true);

    try {
      const { success } = await functionsService.generatePotentialHazards(
        project.id,
        selectedAiModel
      );

      if (!success) {
        console.error("Failed to generate potential hazards.");

        // Track error in analytics
        analyticsService.trackError('hazard_generation_error', 'Generation failed', 'doGenerateHazards');

        toast({
          variant: "destructive",
          title: "Failed to generate potential hazards.",
        });
      } else {
        // Track successful AI hazard generation
        analyticsService.trackAiGeneration('hazards',
          project.context ? project.context.length : 0);

        // Track feature usage
        analyticsService.trackFeatureUsage('generate_hazards', selectedAiModel);
      }
    } catch (error) {
      console.error("Failed to generate potential hazards.", error);

      // Track error in analytics
      analyticsService.trackError('hazard_generation_error', error.message, 'doGenerateHazards');

      toast({
        variant: "destructive",
        title: "Failed to generate potential hazards.",
        description: error.message,
      });
    } finally {
      setIsGeneratingHazards(false);
    }
  }, [project, selectedAiModel, setIsGeneratingHazards, toast]);

  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
  };

  const handleClearFilters = () => {
    setActiveFilters({
      searchTerm: "",
      unmitigatedRisk: [],
      retainedRisk: [],
      riskStatus: [],
      mitigationStatus: [],
    });

    setFilteredHazards(hazards);
  };

  const addNewHazard = () => {
    const newHazard = {
      description: "",
      type: "",
      hazardSource: "",
      causes: [],
      consequences: [],
      riskCategory: [],
      mitigations: [],
      unmitigatedRisk: {
        probability: "",
        severity: "",
        rating: "",
      },
      sfairpStatement: "",
      retainedRisk: {
        probability: "",
        severity: "",
        rating: "",
      },
      residualRiskDescription: "",
      riskStatus: "Open",
      logs: [],
      systems: [],
      operatingModes: [],
      locations: [],
      projectLifecycles: [],
      disciplines: [],
      drawingPackages: [],
      users: [],
      meetings: [],
    };

    setFilteredHazards([newHazard, ...filteredHazards]);
  };

  return (
    <>
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Hazards</CardTitle>
        </CardHeader>

        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              setActiveTab(value);

              localStorage.setItem(
                `ui-preference-${projectId}`,
                JSON.stringify(value)
              );
            }}
          >
            <div className="w-full">
              <div className="grid grid-cols-2">
                <div className="col-span-2 sm:col-span-1">
                  {project && (
                    <Button
                      className="min-w-[135px]"
                      disabled={!project.context || (!isOwner && !isEditor)}
                      onClick={addNewHazard}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      <span>Add hazard</span>
                    </Button>
                  )}

                  <CommonDialog
                    open={showHazardGenerationWarning}
                    setOpen={setShowHazardGenerationWarning}
                    title={"Warning: Duplicate Hazards"}
                    description={
                      "Generating hazards when hazards already exist may create duplicates. Are you sure you want to proceed?"
                    }
                    onConfirm={() => {
                      doGenerateHazards();
                    }}
                  />

                  <Button
                    className="min-w-[135px] ml-4"
                    variant="outline"
                    disabled={
                      isGeneratingHazards ||
                      (project && !project.context) ||
                      (!isOwner && !isEditor)
                    }
                    onClick={() => generateHazards()}
                  >
                    {isGeneratingHazards ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Bot className="h-4 w-4 mr-2" />
                        <span>Generate hazards</span>
                      </>
                    )}
                  </Button>
                </div>

                <div className="flex justify-end col-span-1">
                  <TabsList className="grid grid-cols-2 max-w-[100px]">
                    <TabsTrigger value="list">
                      <List className="h-4 w-4" />
                    </TabsTrigger>

                    <TabsTrigger value="table">
                      <Table className="h-4 w-4" />
                    </TabsTrigger>
                  </TabsList>
                </div>
              </div>
            </div>

            <div className="content-header">
              <HazardSearchFilter
                riskMatrix={riskMatrix}
                hazards={hazards}
                onFilterChange={handleFilterChange}
                activeFilters={activeFilters}
                onClearFilters={handleClearFilters}
              />
            </div>

            {/* List View Tab */}
            <TabsContent value="list">
              <ScrollArea className="min-h-[500px] mt-4">
                <div ref={hazardsListRef}>
                  <HazardsList project={project} hazards={filteredHazards} />
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Table View Tab - No ScrollArea to allow table's own scrolling */}
            <TabsContent value="table" className="mt-4">
              <div 
                className="w-full overflow-hidden" 
                ref={hazardsTableRef}
                style={{ 
                  height: "calc(100vh - 280px)", 
                  minHeight: "400px", 
                  maxHeight: "700px" 
                }}
              >
                <HazardsTable
                  project={project}
                  hazards={filteredHazards}
                  isOwner={isOwner}
                  isEditor={isEditor}
                />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  );
}

export default Hazards;
